/* analytics.js - meetlaag van het thema
 *
 * ARCHITECTUUR (zie tracking\RUNBOOK.md)
 * -------------------------------------
 * De storefront stuurt GEEN advertentie-pixels meer rechtstreeks aan. Alle events lopen via
 * Shopify Customer Events, want dat is de enige laag die ook op de checkout en de bedankpagina
 * draait. Daar pikken drie pixels ze op:
 *
 *   1. Facebook & Instagram-kanaal  -> Meta standaard-events + Conversions API (serverzijde)
 *   2. Google & YouTube-kanaal      -> Google Ads-conversies + GA4
 *   3. Custom pixel "Elusive events" -> onze eigen lead-events (showroom, transportofferte)
 *
 * Deze file doet drie dingen:
 *   - window.trackEvent(naam, params) publiceren naar Shopify.analytics, met een uniek event_id
 *   - consentgebonden configurator- en bevestigde lead-events naar GA4 sturen
 *   - de oude directe tag-bootstrap behouden als noodgreep, maar ALLEEN als iemand de
 *     theme-settings expliciet invult
 *
 * LET OP: laat `meta_pixel_id` en `ga4_id` in de theme-settings LEEG zolang de kanalen uit
 * punt 1 en 2 geinstalleerd zijn. Vul je ze toch in, dan telt elke aankoop dubbel.
 */
(() => {
  'use strict';

  const meta = (name) => document.querySelector(`meta[name="${name}"]`)?.content || '';
  const ga4Id = meta('ga4-id');
  const gtmId = meta('gtm-id');
  const metaPixelId = meta('meta-pixel-id');
  const clarityId = meta('clarity-id');

  // ---- Uniek event_id per gebeurtenis ----
  // Meta ontdubbelt pixel- en serverzijde op dit id. Ook zonder CAPI voorkomt het dat een
  // dubbel afgevuurde klik als twee conversies telt.
  const eventId = () => {
    try {
      if (crypto?.randomUUID) return crypto.randomUUID();
    } catch (e) { /* oudere browser */ }
    return 'evt-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  };

  // ---- Doorgeven aan Shopify Customer Events ----
  const publish = (name, params) => {
    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, params);
        return true;
      }
    } catch (e) { /* sandbox nog niet geladen */ }
    return false;
  };

  // Events die vóór het laden van Shopify.analytics binnenkomen niet weggooien.
  const wachtrij = [];
  let gereed = !!window.Shopify?.analytics?.publish;
  if (!gereed) {
    const timer = setInterval(() => {
      if (!window.Shopify?.analytics?.publish) return;
      clearInterval(timer);
      gereed = true;
      while (wachtrij.length) { const e = wachtrij.shift(); publish(e[0], e[1]); }
    }, 250);
    setTimeout(() => clearInterval(timer), 15000);
  }

  // Custom business events only. Shopify sales channels retain standard ecommerce.
  const customGa4Id = 'G-GSDPN52G28';
  let customGa4Ready = false;
  let privacyLoading = false;
  const privacyPendingEvents = [];
  const privacy = () => window.Shopify?.customerPrivacy;
  const analyticsAllowed = () => {
    try { return privacy()?.analyticsProcessingAllowed?.() === true; }
    catch (_) { return false; }
  };
  const consentState = () => {
    const p = privacy();
    let marketing = false, sharing = false;
    try { marketing = p?.marketingAllowed?.() === true; sharing = p?.saleOfDataAllowed?.() === true; } catch (_) {}
    return {
      analytics_storage: analyticsAllowed() ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing && sharing ? 'granted' : 'denied',
      ad_personalization: marketing && sharing ? 'granted' : 'denied',
    };
  };
  const customGa4 = (name, payload) => {
    let mapped = name;
    if (name === 'view_item' && payload.item_category === 'configurator') mapped = 'configurator_geopend';
    else if (name === 'add_to_cart' && payload.items) mapped = 'configurator_voltooid';
    else if (name === 'generate_lead' && payload.lead_type === 'transportprijs_spanje') mapped = 'transportprijs_aangevraagd';
    else if (name === 'generate_lead' && payload.lead_type === 'offerte') mapped = 'offerte_aangevraagd';
    else if (name === 'generate_lead' && payload.lead_type === 'showroom_afspraak') mapped = 'showroom_afspraak_gepland';
    else if (name === 'generate_lead' && payload.lead_type === 'terugbelverzoek') mapped = 'terugbelverzoek_ingediend';
    else if (!['generate_lead', 'wizard_step_completed', 'configurator_model_selected'].includes(name)) return;
    if (ga4Id) return;
    if (!privacy() && privacyLoading) {
      if (privacyPendingEvents.length < 20) privacyPendingEvents.push([name, payload]);
      return;
    }
    if (!analyticsAllowed()) return;
    if (!customGa4Ready) {
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
        window.gtag('js', new Date());
      }
      window.gtag('consent', 'update', consentState());
      window.gtag('config', customGa4Id, { send_page_view: false, groups: 'bis_custom' });
      if (!document.querySelector('script[data-bis-custom-ga4]')) {
        const script = document.createElement('script');
        script.async = true; script.dataset.bisCustomGa4 = 'true';
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + customGa4Id;
        document.head.appendChild(script);
      }
      customGa4Ready = true;
    }
    const safe = { send_to: customGa4Id, event_id: payload.event_id,
      page_location: window.location.origin + window.location.pathname };
    // Deliberately exclude customer email, phone, addresses and free-text fields.
    for (const key of ['currency', 'value', 'lead_type', 'product_category', 'destination_country', 'transport_price_known', 'step_number', 'model']) {
      const v = payload[key];
      if (typeof v === 'number' || typeof v === 'boolean') safe[key] = v;
      else if (typeof v === 'string' && /^[A-Za-z0-9_ .-]{1,80}$/.test(v)) safe[key] = v;
    }
    if (mapped.startsWith('configurator_')) safe.product_category = 'bed';
    window.gtag('event', mapped, safe);
  };
  document.addEventListener('visitorConsentCollected', () => {
    if (customGa4Ready && window.gtag) window.gtag('consent', 'update', consentState());
  });
  // Loading the existing privacy API does not record or change consent.
  if (!privacy() && window.Shopify?.loadFeatures) {
    privacyLoading = true;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      privacyLoading = false;
      const pending = privacyPendingEvents.splice(0);
      // Drop events if there is no existing permission once the API becomes ready.
      if (error || !analyticsAllowed()) return;
      pending.forEach(([name, payload]) => customGa4(name, payload));
    });
    setTimeout(() => { privacyLoading = false; privacyPendingEvents.length = 0; }, 10000);
  }

  const leadForms = { 'contact-form': 'contactformulier', 'offerte-form': 'offerte', 'transportprijs-form': 'transportprijs_spanje', 'terugbel-form': 'terugbelverzoek' };
  const leadMemory = Object.create(null);
  window.prepareLeadSubmission = function (form) {
    if (!form || !leadForms[form.id]) return;
    const id = eventId();
    const salesSubjects = ['Maatwerk-advies', 'Showroom-bezoek Tilburg', 'B2B / makelaars'];
    const salesInquiry = form.id !== 'contact-form' || salesSubjects.includes(form.querySelector('#contact-subject')?.value);
    leadMemory[form.id] = { pending: id, sales_inquiry: salesInquiry, product_category: form.dataset?.leadCategory || 'onbekend' };
    try { sessionStorage.setItem('bis-lead-' + form.id, JSON.stringify(leadMemory[form.id])); } catch (_) {}
    let input = form.querySelector('[data-lead-reference]');
    if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'contact[Referentie aanvraag]'; input.dataset.leadReference = 'true'; form.appendChild(input); }
    input.value = id;
    // Only report known campaign labels on this request page, with analytics consent.
    // Never copy arbitrary URL parameters, click IDs or personal information into the form.
    let campaignSource = '';
    if (analyticsAllowed()) {
      const query = new URLSearchParams(window.location.search);
      const knownMetaAds = {
        'maat-offerte-20261016': 'Meta / B1 / Maat / 16-10-2026',
        'fabriek-offerte-20261016': 'Meta / B1 / Fabriek / 16-10-2026',
        'marbella-maker-20261016': 'Meta / B1 / Marbella maker / 16-10-2026',
        'marbella-maat-20261016': 'Meta / B1 / Marbella maat / 16-10-2026',
      };
      if (query.get('utm_source') === 'meta' && query.get('utm_medium') === 'paid' &&
          query.get('utm_campaign') === 'b1-nl-in-spanje') {
        campaignSource = Object.prototype.hasOwnProperty.call(knownMetaAds, query.get('utm_content'))
          ? knownMetaAds[query.get('utm_content')] : '';
      }
    }
    let sourceInput = form.querySelector('[data-lead-campaign-source]');
    if (campaignSource && !sourceInput) {
      sourceInput = document.createElement('input'); sourceInput.type = 'hidden';
      sourceInput.name = 'contact[Advertentiebron op aanvraagpagina]';
      sourceInput.dataset.leadCampaignSource = 'true'; form.appendChild(sourceInput);
    }
    if (sourceInput) { sourceInput.value = campaignSource; sourceInput.disabled = !campaignSource; }
  };
  document.addEventListener('submit', (event) => window.prepareLeadSubmission(event.target), true);
  window.trackConfirmedLead = function (formId, params) {
    let record = leadMemory[formId];
    try { record = JSON.parse(sessionStorage.getItem('bis-lead-' + formId) || 'null') || record; } catch (_) {}
    // A confirmation URL alone is not evidence of a submitted inquiry.
    if (!leadForms[formId] || !record?.pending) return;
    // Only count contact sales enquiries with a known submitted subject. No form content is stored.
    if (formId === 'contact-form' && (!record?.pending || record.sales_inquiry !== true)) return;
    const id = record.pending;
    const productCategory = record?.product_category || 'onbekend';
    leadMemory[formId] = { sent: id, product_category: productCategory };
    try { sessionStorage.setItem('bis-lead-' + formId, JSON.stringify(leadMemory[formId])); } catch (_) {}
    window.trackEvent('generate_lead', Object.assign({ lead_type: leadForms[formId], product_category: productCategory, event_id: id }, params || {}));
  };

  /**
   * Centrale event-functie. Overal in het thema aan te roepen:
   *   window.trackEvent('generate_lead', { currency: 'EUR', value: 50, lead_type: 'showroom' })
   */
  window.trackEvent = function (name, params) {
    if (!name) return;
    const payload = Object.assign({ event_id: eventId() }, params || {});

    if (gereed) publish(name, payload);
    else wachtrij.push([name, payload]);

    customGa4(name, payload);

    // dataLayer blijft gevuld zodat GTM later zonder codewijziging aangesloten kan worden
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));

    // Noodgreep: alleen actief als de theme-settings bewust ingevuld zijn
    if (ga4Id && window.gtag) gtag('event', name, payload);
    if (metaPixelId && window.fbq) {
      const fbMap = {
        view_item: 'ViewContent', add_to_cart: 'AddToCart', begin_checkout: 'InitiateCheckout',
        generate_lead: 'Lead', purchase: 'Purchase', search: 'Search',
      };
      if (fbMap[name]) fbq('track', fbMap[name], payload, { eventID: payload.event_id });
      else fbq('trackCustom', name, payload, { eventID: payload.event_id });
    }
  };

  // ---- Wachtrij uit de head leegmaken ----
  // theme.liquid zet een stub neer die events opvangt die vuren voordat deze file geladen is
  // (configurator.js en transport-wizard.js draaien eerder in de defer-volgorde).
  try {
    const vroeg = window.trackEventQueue || [];
    while (vroeg.length) { const e = vroeg.shift(); window.trackEvent(e[0], e[1]); }
    window.trackEventQueue = { push: (e) => window.trackEvent(e[0], e[1]) };
  } catch (e) { /* niets te doen */ }

  // ---- Directe tag-bootstrap (uit tenzij expliciet ingevuld) ----
  if (metaPixelId || ga4Id || gtmId) {
    console.warn('[analytics] Theme-settings bevatten een pixel-ID. Controleer of het ' +
      'Facebook- of Google-kanaal niet dezelfde events al verstuurt, anders telt alles dubbel.');
  }

  if (gtmId) {
    (function (w, d, s, l, i) {
      w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true; j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', gtmId);
  }

  if (ga4Id && !gtmId) {
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + ga4Id;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { dataLayer.push(arguments); };
    gtag('js', new Date());
    gtag('config', ga4Id, { send_page_view: true });
  }

  if (metaPixelId) {
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', metaPixelId);
    fbq('track', 'PageView');
  }

  if (clarityId) {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', clarityId);
  }
})();
